import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../lib/api';
import { ChevronLeft, ChevronRight } from 'lucide-react';

const PAGE_SIZE = 15;

interface WaitlistEntry {
  id: string;
  name: string;
  email: string;
  portfolio_url?: string;
  message?: string;
  desired_site_name?: string;
  status: string;
  created_at: string;
}

export default function WaitlistPage() {
  const navigate = useNavigate();
  const [entries, setEntries] = useState<WaitlistEntry[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState('');
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    api.get<WaitlistEntry[]>('/waitlist')
      .then(setEntries)
      .catch(e => setError(e.message))
      .finally(() => setIsLoading(false));
  }, []);

  async function handleAction(id: string, action: 'approve' | 'reject') {
    setActionLoading(`${id}-${action}`);
    setError('');
    try {
      await api.post(`/waitlist/${id}/${action}`);
      setEntries(prev => prev.map(e => e.id === id ? { ...e, status: action === 'approve' ? 'approved' : 'rejected' } : e));
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Actie mislukt.');
    } finally {
      setActionLoading('');
    }
  }

  const statusBadge = (status: string) => {
    const styles: Record<string, string> = {
      waitlist: 'bg-blue-100 text-blue-700',
      pending: 'bg-blue-100 text-blue-700',
      approved: 'bg-green-100 text-green-700',
      rejected: 'bg-red-100 text-red-700',
    };
    return styles[status] || 'bg-gray-100 text-gray-600';
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Wachtlijst</h1>

      {error && <div className="p-4 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>}

      <div className="card overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Naam</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">E-mail</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Portfolio</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Status</th>
              <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Datum</th>
              <th className="px-6 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 bg-white">
            {isLoading ? (
              [...Array(5)].map((_, i) => (
                <tr key={i}>
                  {[...Array(6)].map((__, j) => (
                    <td key={j} className="px-6 py-4"><div className="h-4 bg-gray-100 rounded animate-pulse" /></td>
                  ))}
                </tr>
              ))
            ) : entries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).length === 0 ? (
              <tr>
                <td colSpan={6} className="px-6 py-12 text-center text-sm text-gray-400">Geen wachtlijst-aanmeldingen.</td>
              </tr>
            ) : (
              entries.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE).map(entry => (
                <tr key={entry.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <p className="text-sm font-medium text-gray-900">{entry.name}</p>
                    {entry.message && <p className="text-xs text-gray-400 mt-1 max-w-xs truncate">{entry.message}</p>}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-700">{entry.email}</td>
                  <td className="px-6 py-4 text-sm">
                    {entry.portfolio_url ? (
                      <a href={entry.portfolio_url} target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline truncate block max-w-[200px]">
                        {entry.portfolio_url}
                      </a>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${statusBadge(entry.status)}`}>
                      {entry.status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-500">
                    {new Date(entry.created_at).toLocaleDateString('nl-NL')}
                  </td>
                  <td className="px-6 py-4 text-right">
                    {(entry.status === 'waitlist' || entry.status === 'pending') && (
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => navigate(`/admin/provision/${entry.id}`, {
                            state: {
                              waitlistUser: {
                                id: entry.id,
                                name: entry.name,
                                email: entry.email,
                                desired_site_name: entry.desired_site_name,
                              },
                            },
                          })}
                          disabled={!!actionLoading}
                          className="px-3 py-1 text-xs font-medium text-white bg-green-600 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                        >
                          Goedkeuren
                        </button>
                        <button
                          onClick={() => handleAction(entry.id, 'reject')}
                          disabled={!!actionLoading}
                          className="px-3 py-1 text-xs font-medium text-white bg-red-600 rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
                        >
                          {actionLoading === `${entry.id}-reject` ? '...' : 'Afwijzen'}
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
        {entries.length > PAGE_SIZE && (() => {
          const totalPages = Math.ceil(entries.length / PAGE_SIZE);
          return (
            <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100">
              <span className="text-xs text-gray-400">{entries.length} aanmelding{entries.length !== 1 ? 'en' : ''}</span>
              <div className="flex items-center gap-1">
                <button onClick={() => setPage(p => Math.max(1, p - 1))} disabled={page === 1}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronLeft className="w-4 h-4" />
                </button>
                <span className="text-xs text-gray-500 px-2">{page} / {totalPages}</span>
                <button onClick={() => setPage(p => Math.min(totalPages, p + 1))} disabled={page === totalPages}
                  className="p-1.5 rounded-lg text-gray-400 hover:text-gray-700 hover:bg-gray-100 disabled:opacity-30 disabled:cursor-not-allowed transition-colors">
                  <ChevronRight className="w-4 h-4" />
                </button>
              </div>
            </div>
          );
        })()}
      </div>
    </div>
  );
}
